import { useRef, useState } from "react";
import { AlertCircle, FileText, FileUp, Upload, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatFileSize } from "@/lib/format";

interface FileDropzoneRenderProps {
  openPicker: () => void;
  isDragging: boolean;
}

type SingleProps = {
  multiple?: false;
  onFileSelect: (file: File) => void | Promise<void>;
  onFilesSelect?: never;
};

type MultipleProps = {
  multiple: true;
  onFilesSelect: (files: File[]) => void | Promise<void>;
  onFileSelect?: never;
};

type FileDropzoneProps = {
  accept: string;
  acceptDescription: string;
  disabled?: boolean;
  className?: string;
  title?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children?: (props: FileDropzoneRenderProps) => React.ReactNode;
} & (SingleProps | MultipleProps);

const tokensFromAccept = (accept: string): string[] =>
  accept
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

const matchesMimePattern = (fileType: string, pattern: string): boolean => {
  if (pattern === fileType) return true;
  const [pType, pSub] = pattern.split("/");
  const [fType, fSub] = fileType.split("/");
  if (pType !== fType) return false;
  return pSub === "*" || pSub === fSub;
};

const isFileAccepted = (file: File, accept: string): boolean => {
  const tokens = tokensFromAccept(accept);
  if (tokens.length === 0) return true;
  const fileExt = file.name.split(".").pop()?.toLowerCase();
  const fileType = file.type.toLowerCase();
  return tokens.some((token) => {
    if (token.includes("/")) {
      return fileType ? matchesMimePattern(fileType, token) : false;
    }
    const ext = token.replace(/^\./, "");
    return !!fileExt && ext === fileExt;
  });
};

export const FileDropzone = (props: FileDropzoneProps) => {
  const {
    accept,
    acceptDescription,
    disabled = false,
    className,
    title = "Select a file",
    subtitle,
    icon,
    children,
    multiple,
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dropError, setDropError] = useState<string | null>(null);
  const [selected, setSelected] = useState<File[]>([]);

  const openPicker = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleFiles = async (fileList: FileList | null | undefined) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    const rejected = files.filter((f) => !isFileAccepted(f, accept));
    if (rejected.length > 0) {
      setDropError(`File must be ${acceptDescription}.`);
      return;
    }
    setDropError(null);
    setSelected(multiple ? files : [files[0]]);
    if (multiple) {
      await props.onFilesSelect!(files);
    } else {
      await props.onFileSelect!(files[0]);
    }
  };

  const clearSelection = () => {
    setSelected([]);
    setDropError(null);
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await handleFiles(e.target.files);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDrop = async (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    await handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    if (disabled) return;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
  };

  const dndHandlers = {
    onDrop: handleDrop,
    onDragOver: handleDragOver,
    onDragEnter: handleDragOver,
    onDragLeave: handleDragLeave,
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        tabIndex={-1}
        onChange={handleInputChange}
        disabled={disabled}
      />
      {children ? (
        <div className="relative rounded-md" {...dndHandlers}>
          {children({ openPicker, isDragging })}
          {isDragging && (
            <div className="absolute inset-0 rounded-md border-2 border-dashed border-primary bg-background/50 backdrop-blur-sm flex flex-col items-center justify-center gap-2 pointer-events-none">
              <Upload className="h-8 w-8" />
              <span className="text-sm font-medium">
                Drop {acceptDescription} to upload
              </span>
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          {...dndHandlers}
          disabled={disabled}
          aria-label={`${title}, drag and drop or click to select`}
          className={cn(
            "w-full py-10 px-4 xs:px-10 border-2 border-dashed rounded-md flex flex-col items-center justify-center gap-2 transition-colors text-left",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            isDragging
              ? "border-primary bg-primary/5"
              : "border-input hover:bg-muted",
            disabled && "cursor-not-allowed opacity-60",
            className,
          )}
        >
          {isDragging ? (
            <Upload className="h-8 w-8 text-primary" />
          ) : (
            (icon ?? <FileUp className="h-8 w-8 text-muted-foreground" />)
          )}
          <span className="text-sm text-muted-foreground text-center">
            {isDragging ? `Drop ${acceptDescription} to upload` : title}
          </span>
          {subtitle && (
            <span className="text-xs text-muted-foreground text-center">
              {subtitle}
            </span>
          )}
          <span className="text-xs text-muted-foreground text-center">
            It must be {acceptDescription}.
          </span>
        </button>
      )}
      {!children && selected.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1.5">
          {selected.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              className="flex items-center gap-2 p-2 border rounded-md bg-muted/50 text-sm"
            >
              <FileText className="h-4 w-4 text-primary shrink-0" />
              <span
                className="font-medium truncate flex-1 min-w-0"
                title={f.name}
              >
                {f.name}
              </span>
              <span className="text-xs text-muted-foreground shrink-0">
                {formatFileSize(f.size)}
              </span>
              {selected.length === 1 && (
                <button
                  type="button"
                  onClick={clearSelection}
                  disabled={disabled}
                  aria-label="Clear selection"
                  className="text-muted-foreground hover:text-foreground disabled:opacity-50"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {dropError && (
        <div className="mt-2 bg-destructive border border-destructive rounded-sm py-1.5 px-3 gap-1 items-center text-white w-full flex">
          <AlertCircle className="inline h-3.5 w-3.5 shrink-0" />
          <span className="ml-1">{dropError}</span>
        </div>
      )}
    </>
  );
};
